// @ts-check
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import './ActionMenu.css';

/**
 * @typedef {Object} OverflowItem
 * @property {string} key
 * @property {string} label
 * @property {() => void} onClick
 * @property {boolean} [disabled]
 * @property {string} [title]
 * @property {boolean} [danger] render in the destructive group (below a separator)
 */

/**
 * Per-row action control: one inline primary action plus a "⋯" overflow menu
 * for the rest. Replaces the original 7-button row. Fully keyboard operable:
 * Enter/Space/↓ opens, ↑/↓ navigate, Enter/Space activate, Esc closes and
 * returns focus to the trigger, Tab closes and moves on.
 *
 * @param {{
 *   inline: { label: string, onClick: () => void, variant?: string, disabled?: boolean },
 *   items: OverflowItem[],
 *   loading?: boolean,
 *   label: string,
 * }} props
 */
export function ActionMenu({ inline, items, loading, label }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef(/** @type {HTMLButtonElement|null} */ (null));
  const menuRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const itemRefs = useRef(/** @type {Array<HTMLButtonElement|null>} */ ([]));
  const menuId = useId();

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, []);

  const openMenu = useCallback((index = 0) => {
    setActiveIndex(index);
    setOpen(true);
  }, []);

  // Move focus to the active menu item whenever the menu opens or moves.
  useEffect(() => {
    if (open) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [open, activeIndex]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const onTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(items.length - 1);
    }
  };

  const onMenuKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(items.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        // Allow focus to leave naturally, but collapse the menu.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const runItem = (item) => {
    if (item.disabled) return;
    item.onClick();
    close(true);
  };

  return (
    <div className="action-menu">
      <Button
        size="sm"
        variant={inline.variant || 'ghost'}
        onClick={inline.onClick}
        disabled={loading || inline.disabled}
      >
        {inline.label}
      </Button>

      {items.length > 0 && (
        <div className="action-menu__overflow">
          <button
            ref={triggerRef}
            type="button"
            className="action-menu__trigger"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={label}
            disabled={loading}
            onClick={() => (open ? close(false) : openMenu(0))}
            onKeyDown={onTriggerKeyDown}
          >
            <span aria-hidden="true">⋯</span>
          </button>

          {open && (
            <div
              ref={menuRef}
              role="menu"
              id={menuId}
              tabIndex={-1}
              className="action-menu__list"
              aria-label={label}
              onKeyDown={onMenuKeyDown}
            >
              {items.map((item, index) => (
                <React.Fragment key={item.key}>
                  {item.danger && index > 0 && !items[index - 1].danger && (
                    <div className="action-menu__separator" role="separator" />
                  )}
                  <button
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    type="button"
                    role="menuitem"
                    tabIndex={index === activeIndex ? 0 : -1}
                    className={`action-menu__item${item.danger ? ' action-menu__item--danger' : ''}`}
                    disabled={item.disabled}
                    title={item.title}
                    onClick={() => runItem(item)}
                  >
                    {item.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ActionMenu;
